import { FilaCPP, formatearBs, formatearNumero } from '@/lib/cpp';

const tituloDetalle = (fila: FilaCPP): string => {
  if (fila.esInicial || fila.tipo === 'inicial') return 'Inventario inicial';
  if (fila.motivo.toLowerCase().includes('inventario inicial')) return 'Inventario inicial';
  if (fila.motivo.toLowerCase().includes('venta')) return 'Venta';
  if (fila.motivo.toLowerCase().includes('compra')) return 'Compra';
  return fila.motivo || 'Movimiento';
};

const filaStyle = (fila: FilaCPP): string => {
  if (fila.esInicial || fila.tipo === 'inicial') return 'bg-[#F4F6F8]';
  if (fila.tipo === 'entrada') {
    if (fila.motivo.toLowerCase().includes('inventario inicial')) return 'bg-[#F0F9FF]';
    if (fila.motivo.toLowerCase().includes('compra')) return 'bg-[#F0FDF4]';
    return 'bg-[#FFFBEB]';
  }
  if (fila.motivo.toLowerCase().includes('venta')) return 'bg-[#FEF2F2]';
  return 'bg-[#FFFBEB]';
};

const badgeStyle = (fila: FilaCPP): string => {
  if (fila.esInicial || fila.tipo === 'inicial') return 'bg-gray-200 text-gray-600';
  if (fila.tipo === 'entrada') {
    if (fila.motivo.toLowerCase().includes('compra')) return 'bg-[#16A34A]/15 text-[#16A34A]';
    return 'bg-[#2563EB]/15 text-[#2563EB]';
  }
  return 'bg-[#DC2626]/15 text-[#DC2626]';
};

const badgeLabel = (fila: FilaCPP): string => {
  if (fila.esInicial || fila.tipo === 'inicial') return 'Inicial';
  return fila.tipo === 'entrada' ? 'Entrada' : 'Salida';
};

const fechaCorta = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function TablaCPP({ filas }: { filas: FilaCPP[] }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-[#F9FAFB]">
              <th rowSpan={2} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase whitespace-nowrap border-r border-gray-100">
                Detalle
              </th>
              <th colSpan={3} className="text-center px-4 py-2 text-xs font-semibold text-gray-600 uppercase border-r border-gray-100">
                Control físico
              </th>
              <th rowSpan={2} className="text-center px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase whitespace-nowrap border-r border-gray-100">
                CPP
              </th>
              <th colSpan={3} className="text-center px-4 py-2 text-xs font-semibold text-gray-600 uppercase">
                Control valorado
              </th>
            </tr>
            <tr className="border-b border-gray-100 bg-[#F9FAFB]">
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap border-r border-gray-100">Entrada</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap border-r border-gray-100">Salida</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap border-r border-gray-100">Saldo</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap border-r border-gray-100">Entrada</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap border-r border-gray-100">Salida</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-gray-500 whitespace-nowrap">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((fila) => (
              <tr key={fila.id} className={`border-b border-gray-50 ${filaStyle(fila)}`}>
                <td className="px-4 py-3 border-r border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${badgeStyle(fila)}`}>
                      {badgeLabel(fila)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#111827] whitespace-nowrap">{tituloDetalle(fila)}</p>
                      <p className="text-[11px] text-gray-400 whitespace-nowrap">
                        {fechaCorta(fila.fecha)}
                        {fila.referencia ? ` · ${fila.referencia}` : ''}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100">
                  {fila.entradaFisica > 0 ? (
                    <span className="font-semibold text-[#16A34A]">+{formatearNumero(fila.entradaFisica)}</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100">
                  {fila.salidaFisica > 0 ? (
                    <span className="font-semibold text-[#DC2626]">-{formatearNumero(fila.salidaFisica)}</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100">
                  <span className="font-bold text-[#111827]">{formatearNumero(fila.saldoFisico)}</span>
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100 text-[#2563EB] font-medium">
                  {formatearBs(fila.cpp)}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100 text-[#16A34A]">
                  {fila.entradaValor > 0 ? formatearBs(fila.entradaValor) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap border-r border-gray-100 text-[#DC2626]">
                  {fila.salidaValor > 0 ? formatearBs(fila.salidaValor) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <span className="font-bold text-[#111827]">{formatearBs(fila.saldoValor)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}